import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, ChevronDown, Sparkles } from 'lucide-react';
import { InteriorMenuItem } from '../../types/interiorConfig';
import { useTheme } from '../styles/ThemeContext';
// Items that have options configured for demo
const DEMO_ITEMS = new Set(['refrigerator', 'range-oven', 'dishwasher', 'microwave', 'countertop']);
interface InteriorSidebarProps {
  menuData: InteriorMenuItem[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelectItem: (item: InteriorMenuItem) => void;
  selectedItemId?: string;
}
export function InteriorSidebar({
  menuData,
  isCollapsed,
  onToggleCollapse,
  onSelectItem,
  selectedItemId
}: InteriorSidebarProps) {
  const {
    isDark
  } = useTheme();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['main-options', 'kitchen']));
  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };
  const renderMenuItem = (item: InteriorMenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.id);
    const isSelected = selectedItemId === item.id;
    const isCategory = item.type === 'category';
    const isDemoItem = DEMO_ITEMS.has(item.id);
    return <div key={item.id} className="relative" data-id="element-289">
        <button onClick={() => {
        if (hasChildren) {
          toggleExpand(item.id);
        }
        if (item.options || !hasChildren) {
          onSelectItem(item);
        }
      }} className={`w-full flex items-center gap-3 px-6 py-2.5 text-left transition-all group relative ${isCategory ? 'mt-6 mb-2' : isSelected ? isDark ? 'bg-gradient-to-r from-cyan-500/20 to-transparent border-l-2 border-cyan-500' : 'bg-gradient-to-r from-teal-500/20 to-transparent border-l-2 border-teal-600' : isDemoItem ? isDark ? 'hover:bg-amber-500/10 border-l-2 border-transparent' : 'hover:bg-amber-500/10 border-l-2 border-transparent' : isDark ? 'hover:bg-white/5 border-l-2 border-transparent' : 'hover:bg-white/30 border-l-2 border-transparent'}`} style={{
        paddingLeft: isCategory ? '24px' : `${level * 12 + 24}px`
      }} data-id="element-290">
          {/* Category Header Style */}
          {isCategory ? <span className={`uppercase text-[10px] tracking-[0.2em] font-bold ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-id="element-291">
              {item.name}
            </span> : <>
              {/* Item Style */}
              <div className={`w-1.5 h-1.5 rounded-full transition-colors ${isSelected ? isDark ? 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]' : 'bg-teal-600 shadow-[0_0_8px_rgba(13,148,136,0.4)]' : isDemoItem ? 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.5)]' : isDark ? 'bg-white/20 group-hover:bg-white/40' : 'bg-gray-400 group-hover:bg-gray-600'}`} data-id="element-292" />
              <span className={`flex-1 text-sm font-medium tracking-wide flex items-center gap-2 ${isSelected ? isDark ? 'text-white' : 'text-gray-900' : isDemoItem ? 'text-amber-400' : isDark ? 'text-white/70 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'}`} data-id="element-293">
                {item.name}
                {isDemoItem && <Sparkles className="w-3.5 h-3.5 text-amber-400" data-id="element-294" />}
              </span>

              {/* Chevron */}
              {hasChildren && <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isDark ? 'text-white/30' : 'text-gray-400'} ${isExpanded ? 'rotate-180' : ''}`} data-id="element-295" />}
            </>}
        </button>

        {/* Children */}
        <AnimatePresence data-id="element-296">
          {hasChildren && isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-297">
              {item.children!.map(child => renderMenuItem(child, level + 1))}
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <>
      {/* Sidebar */}
      <motion.div initial={false} animate={{
      width: isCollapsed ? 0 : 320,
      x: isCollapsed ? -320 : 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className={`absolute top-0 left-0 bottom-0 z-40 backdrop-blur-2xl shadow-2xl overflow-hidden flex flex-col transition-colors duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40'}`} data-id="element-298">
        {/* Header with Toggle */}
        <div className="p-6 pb-4" data-id="element-299">
          <div className={`flex p-1 rounded-lg border ${isDark ? 'bg-slate-900/50 border-white/5' : 'bg-white/50 border-white/40'}`} data-id="element-300">
            <button className={`flex-1 px-4 py-2 text-[11px] font-bold rounded shadow-lg uppercase tracking-wider transition-all ${isDark ? 'bg-cyan-600 text-white' : 'bg-teal-600 text-white'}`} data-id="element-301">
              Interior
            </button>
            <button className={`flex-1 px-4 py-2 text-[11px] font-bold rounded uppercase tracking-wider transition-colors ${isDark ? 'text-white/40 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-302">
              Exterior
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar py-2" data-id="element-303">
          {menuData.map(item => renderMenuItem(item))}
        </div>

        {/* Footer Logo */}
        <div className={`p-6 bg-gradient-to-t transition-colors duration-300 ${isDark ? 'border-t border-white/5 from-black/40 to-transparent' : 'border-t border-white/40 from-white/40 to-transparent'}`} data-id="element-304">
          <img src="https://cdn.magicpatterns.com/uploads/pf6RkFzoyX8RpYXPcVUgLU/landmark-homes.jpg" alt="Landmark Homes" className="h-16 w-auto mx-auto opacity-50 rounded" data-id="element-305" />
        </div>
      </motion.div>

      {/* Collapse Toggle */}
      <motion.button animate={{
      left: isCollapsed ? 0 : 320
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} onClick={onToggleCollapse} className={`absolute top-6 z-50 p-1.5 rounded-r-md border border-l-0 shadow-xl transition-colors ${isDark ? 'bg-slate-900/70 text-white/50 hover:text-white border-white/10' : 'bg-white/70 text-gray-500 hover:text-gray-900 border-white/40'}`} data-id="element-306">
        {isCollapsed ? <ChevronRight className="w-4 h-4" data-id="element-307" /> : <ChevronLeft className="w-4 h-4" data-id="element-308" />}
      </motion.button>
    </>;
}